import React, { useState, useRef, useEffect } from "react";
import { Play, X, Clock, Download, Camera, Volume2 } from "lucide-react";

// Hosting convention: drop the recorded MP4 into /app/frontend/public/videos/
// with the matching filename and the card auto-promotes from
// "Recording in production" to a playable video.
const VIDEOS = [
  {
    id: "01",
    file: "01-regimen.mp4",
    title: "Building a chemotherapy regimen — without duplicates",
    duration: "00:48",
    blurb: "Watch a Doctor compose AC-T from the 187-drug catalog. The dedup engine catches an attempted duplicate from another provider in real time.",
  },
  {
    id: "02",
    file: "02-treatment-calendar.mp4",
    title: "Treatment calendar in three clicks",
    duration: "00:52",
    blurb: "Attach a regimen, set a start date, watch six cycles + lab draws + clinic visits drop onto the patient's calendar instantly.",
  },
  {
    id: "03",
    file: "03-reschedule-request.mp4",
    title: "Reschedule request: patient to provider in one tap",
    duration: "00:55",
    blurb: "Patient taps Reschedule on an appointment → the provider's Approvals queue lights up → decision fires SMS + email + in-app notification.",
  },
  {
    id: "04",
    file: "04-approvals-queue.mp4",
    title: "Approval queue: every request in one place",
    duration: "00:30",
    blurb: "Reschedules, refills, pause-cycle, and side-effect concerns — all unified into one Provider Approvals view with one-click resolution.",
  },
];

// Probe whether each MP4 actually exists on the server. While the file is
// still being recorded, the card flips to a "Recording in production" badge
// instead of showing a broken video element.
function useVideoAvailability(videos) {
  const [available, setAvailable] = useState({});
  useEffect(() => {
    let cancelled = false;
    videos.forEach((v) => {
      fetch(`/videos/${v.file}`, { method: "HEAD" })
        .then((r) => {
          // The React SPA dev server returns 200 + text/html for any unknown
          // path (index.html fallback). Only treat the MP4 as "present" when
          // the response is an actual video — check both status and content-type.
          const ct = r.headers.get("content-type") || "";
          const ok = r.ok && ct.startsWith("video/");
          if (!cancelled) setAvailable((prev) => ({ ...prev, [v.id]: ok }));
        })
        .catch(() => { if (!cancelled) setAvailable((prev) => ({ ...prev, [v.id]: false })); });
    });
    return () => { cancelled = true; };
  }, [videos]);
  return available;
}

export default function VideosPage() {
  const [playing, setPlaying] = useState(null);
  const available = useVideoAvailability(VIDEOS);

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-16 pb-12 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Videos &middot; application capabilities</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl mx-auto">See OncoCadence in action.</h1>
        <p className="text-slate-400 mt-4 max-w-2xl mx-auto">Four short walkthroughs covering every feature. Click any card to play &mdash; or open the gallery to download MP4s for LinkedIn, WhatsApp, or email.</p>
        <a href="/videos/all" className="inline-flex items-center gap-1.5 mt-5 text-sm font-bold text-emerald-400 hover:text-emerald-300" data-testid="open-gallery-link">
          Open the full gallery &rarr;
        </a>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pb-20 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {VIDEOS.map((v, i) => {
          const ready = available[v.id] === true;
          return (
            <div key={v.id} className="glass-card glass-card-hover overflow-hidden fade-up" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`video-tile-${i}`}>
              <button
                onClick={() => setPlaying(v)}
                className="relative aspect-video w-full bg-gradient-to-br from-emerald-500/20 to-[#0B1220] flex items-center justify-center overflow-hidden"
                data-testid={`video-tile-button-${i}`}
              >
                <div className="absolute inset-0 marketing-3d-ring opacity-40">
                  <div className="ring r1" /><div className="ring r2" /><div className="ring r3" /><div className="ring r4" />
                </div>
                <div className="relative w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center shadow-2xl shadow-emerald-500/50 hover:scale-110 transition-transform">
                  <Play className="w-7 h-7 text-[#0B1220] ml-1" fill="currentColor" />
                </div>
                {!ready && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 text-[10px] font-bold text-amber-300 uppercase tracking-wider">
                    <Camera className="w-3 h-3" />Recording in production
                  </span>
                )}
                <span className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded bg-black/60 text-[11px] text-slate-300 font-mono">
                  <Clock className="w-3 h-3" />{v.duration}
                </span>
              </button>
              <div className="p-5">
                <h3 className="font-bold text-base mb-1.5 leading-snug">{v.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{v.blurb}</p>
                <div className="mt-3 flex items-center gap-4 text-xs font-bold">
                  <button onClick={() => setPlaying(v)} className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300" data-testid={`video-play-${i}`}>
                    <Play className="w-3.5 h-3.5" fill="currentColor" />Watch &middot; {v.duration}
                  </button>
                  {ready && (
                    <a href={`/videos/${v.file}`} download className="inline-flex items-center gap-1.5 text-slate-400 hover:text-emerald-400" data-testid={`video-download-${i}`}>
                      <Download className="w-3.5 h-3.5" />MP4
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {playing && <PlayerModal video={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}

function PlayerModal({ video, onClose }) {
  const ref = useRef(null);
  const [err, setErr] = useState(null);
  const [muted, setMuted] = useState(true);

  // Muted autoplay is permitted by every modern browser and never gets paused
  // mid-stream. The user un-mutes via the native controls (or our overlay
  // button) — that click is a fresh user gesture so audio playback starts
  // without being throttled.
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {});
  }, []);

  const unmute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    v.play().catch(() => {});
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B1220]/90 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="video-player-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0B1220] rounded-2xl w-full max-w-4xl overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between gap-4 border-b border-white/[0.06]">
          <h3 className="font-bold text-base truncate">{video.title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white shrink-0" aria-label="Close" data-testid="video-player-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="relative">
          <video
            ref={ref}
            src={`/videos/${video.file}`}
            controls
            autoPlay
            muted
            playsInline
            preload="auto"
            onError={() => setErr(true)}
            className="w-full aspect-video bg-black"
            data-testid="video-player"
          >
            Your browser doesn&apos;t support HTML5 video. <a href={`/videos/${video.file}`} className="text-emerald-400">Download the MP4</a>.
          </video>
          {muted && !err && (
            <button
              onClick={unmute}
              className="absolute top-3 right-3 flex items-center gap-2 px-3.5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] text-xs font-bold shadow-2xl shadow-emerald-500/40 backdrop-blur"
              data-testid="video-unmute"
            >
              <Volume2 className="w-3.5 h-3.5" />Tap for sound
            </button>
          )}
        </div>
        {err && (
          <div className="px-5 py-3 text-xs text-amber-300/90 bg-amber-500/[0.06] border-b border-white/[0.06]">
            Your browser can&apos;t play this MP4 inline. <a href={`/videos/${video.file}`} download className="font-bold underline">Download the file</a> to watch it locally.
          </div>
        )}
        <div className="px-5 py-3 flex items-center justify-end gap-3 border-t border-white/[0.06] text-sm">
          <a href={`/videos/${video.file}`} download className="inline-flex items-center gap-1.5 text-slate-300 hover:text-emerald-400" data-testid="video-player-download">
            <Download className="w-4 h-4" />Download MP4
          </a>
        </div>
      </div>
    </div>
  );
}
